import { ChevronDown, ChevronRight, History, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { listRecentModerationActions } from '../api/moderation';
import type { ModerationActionRow } from '../api/moderation';
import { cn } from '@/lib/utils';

/** Poll cadence while the section is open: the audit is server-side, so a soft poll converges
 * on other staff members' actions too (the refreshKey bump covers the local moderator's own). */
const RECENT_POLL_MS = 10_000;

/** The audit vocabulary words the list renders; unknown future words fall back to the raw word. */
const KNOWN_ACTIONS: ReadonlySet<string> = new Set([
  'mute',
  'mute_all',
  'kick',
  'stop_share',
  'promote',
  'demote',
  'speak_granted',
  'speak_revoked',
  'board_granted',
  'board_revoked',
]);

export interface RecentActionsProps {
  /** The room slug (the API path segment). */
  readonly room: string;
  /** Current join JWT for the Bearer call; null while no token is held (fetches skip). */
  readonly getToken: () => string | null;
  /**
   * Bumped by the page after the local moderator dispatches an action: the audit row lands
   * server-side a moment later, so the bump schedules one out-of-band refresh.
   */
  readonly refreshKey: number;
  /** Poll interval override for tests; production keeps 10 s. */
  readonly pollMs?: number | undefined;
}

/** Relative-time label of an audit stamp (minutes/hours; older renders the clock time). */
export function formatActionTime(iso: string, locale: string): string {
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) {
    return iso;
  }
  const deltaMs = Date.now() - at;
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (deltaMs < 60_000) {
      return rtf.format(-Math.max(0, Math.round(deltaMs / 1000)), 'second');
    }
    if (deltaMs < 3_600_000) {
      return rtf.format(-Math.round(deltaMs / 60_000), 'minute');
    }
    if (deltaMs < 24 * 3_600_000) {
      return rtf.format(-Math.round(deltaMs / 3_600_000), 'hour');
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(at),
    );
  } catch {
    return iso;
  }
}

/**
 * The collapsible «Recent actions» section of the participants tab (M4 item 5, CR-22): the
 * room's last moderation audit rows with localized action names, relative time and the
 * actor/target identities. Staff-only data (the page gates the prop), collapsed by default;
 * expanding fetches, an open section polls softly (10 s) so other staff members' actions
 * converge, and a refreshKey bump re-fetches after the local moderator acts. Collapsed, the
 * section costs nothing - no fetch, no timer.
 */
export function RecentActions(props: RecentActionsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<readonly ModerationActionRow[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // A refresh that lost the race against a newer one must not overwrite the fresher list.
  const refreshSeqRef = useRef(0);
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = props.getToken();

  const refresh = useCallback(async (): Promise<void> => {
    const token = tokenRef.current;
    if (token === null) {
      return;
    }
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    try {
      const list = await listRecentModerationActions(props.room, token);
      if (refreshSeqRef.current === seq) {
        setRows(list);
        setLoadFailed(false);
      }
    } catch {
      if (refreshSeqRef.current === seq) {
        setLoadFailed(true);
      }
    }
  }, [props.room]);

  // Expand fetches; the refreshKey bump re-fetches while open (the local moderator just acted).
  useEffect(() => {
    if (!open) {
      return;
    }
    void refresh();
  }, [open, refresh, props.refreshKey]);

  // Soft poll while open (and only open - collapsed the section costs nothing).
  const pollMs = props.pollMs ?? RECENT_POLL_MS;
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void refresh();
    }, pollMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [open, pollMs, refresh]);

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  return (
    <section
      data-testid="recent-actions"
      aria-label={t('moderation.recent_aria')}
      className="shrink-0 border-t border-white/[0.06]"
    >
      <div className="flex items-stretch">
        <button
          type="button"
          data-testid="recent-actions-toggle"
          aria-expanded={open}
          onClick={() => {
            setOpen((current) => !current);
          }}
          className="flex h-9 min-w-0 flex-1 items-center gap-1.5 px-3 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500 transition-colors hover:bg-white/[0.04] hover:text-zinc-300"
        >
          {open ? (
            <ChevronDown aria-hidden className="size-3" />
          ) : (
            <ChevronRight aria-hidden className="size-3" />
          )}
          <History aria-hidden className="size-3" />
          {t('moderation.recent_title')}
          {rows !== null && rows.length > 0 ? (
            <span className="ml-1 tabular-nums text-zinc-600">{rows.length}</span>
          ) : null}
        </button>
        {open ? (
          <button
            type="button"
            data-testid="recent-actions-refresh"
            aria-label={t('moderation.recent_refresh_aria')}
            title={t('moderation.recent_refresh_aria')}
            onClick={() => {
              void refresh();
            }}
            className="grid w-9 shrink-0 place-items-center text-zinc-500 transition-colors hover:bg-white/10 hover:text-white"
          >
            <RefreshCw aria-hidden className="size-3" />
          </button>
        ) : null}
      </div>
      {open ? (
        <div
          data-testid="recent-actions-list"
          className="max-h-44 overflow-y-auto px-3 pb-2 scroll-thin-dark"
        >
          {loadFailed ? (
            <p role="alert" data-testid="recent-actions-error" className="py-2 text-[11px] text-red-300">
              {t('moderation.recent_failed')}
            </p>
          ) : rows === null ? (
            <p className="flex items-center gap-1.5 py-2 text-[11px] text-zinc-500">
              <Loader2 aria-hidden className="size-3 animate-spin" />
              {t('moderation.recent_loading')}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-2 text-[11px] text-zinc-600">{t('moderation.recent_empty')}</p>
          ) : (
            <ul className="space-y-0.5" aria-label={t('moderation.recent_aria')}>
              {rows.map((row) => (
                <li
                  key={row.id}
                  data-testid={`recent-action-${String(row.id)}`}
                  className="flex items-baseline gap-1.5 rounded-md px-1 py-1 text-[11px] leading-tight hover:bg-white/[0.03]"
                >
                  <span
                    data-testid={`recent-action-kind-${String(row.id)}`}
                    className={cn(
                      'shrink-0 font-semibold',
                      row.action === 'kick' ? 'text-red-300' : 'text-zinc-200',
                    )}
                  >
                    {t(
                      KNOWN_ACTIONS.has(row.action)
                        ? `moderation.recent_action_${row.action}`
                        : 'moderation.recent_action_other',
                      { action: row.action },
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-zinc-500">
                    {row.target !== null
                      ? t('moderation.recent_line_target', {
                          actor: row.actor,
                          target: row.target,
                        })
                      : t('moderation.recent_line_room', { actor: row.actor })}
                  </span>
                  <time dateTime={row.at} className="shrink-0 tabular-nums text-zinc-600">
                    {formatActionTime(row.at, locale)}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}
